import { Scene } from '@babylonjs/core/scene';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { CreateTube } from '@babylonjs/core/Meshes/Builders/tubeBuilder';
import { CreateTorus } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { Ability } from '../../systems/AbilitySystem';
import { gameEvents } from '../../core/EventBus';
import { GameEvents } from '../../config/events';
import { COLORS } from '../../config/GameConfig';
import { resolveTttWindow } from './tttWindow';
import { AccessibilitySystem } from '../../systems/AccessibilitySystem';
import { qaRulesActive } from '../../qa/qaRules';

/**
 * Turn-Table Touch — Basil's high-speed paw dexterity (Babylon.js port).
 *
 * A rhythm/timing mini-game that activates when Basil interacts
 * with dials, gears, or generators.
 *
 * Mechanic:
 *   1. Activate near an interactive dial/gear
 *   2. A circular timing ring appears with a marker orbiting it
 *   3. Press the interact key when the marker is in the "hit zone"
 *   4. Good timing = successful spin. Bad timing = gear jams / resets.
 *   5. Multiple successful hits build a combo multiplier
 *
 * Visual: brass timing ring with copper marker, hit zone glows
 * green on success, red on miss.
 */
export interface TurnTableTouchConfig {
  /** World-space display radius; timing and angular hit windows are unchanged. */
  visualRadius?: number;
  /** Camera-facing display offset, keeping the marker clear of prop geometry. */
  visualDepthOffset?: number;
  /** Marker orbit speed in radians per second. Default 2.5. */
  markerSpeedRadPerSec?: number;
  /**
   * Hit zone half-width in radians, centered at the bottom of the ring
   * (angle -π/2). Default 0.4 (0.8 rad total window ~12.7% of the cycle).
   * Level 0 uses 0.8 (1.6 rad total) for a forgiving tutorial window.
   */
  hitZoneHalfWidthRad?: number;
  /**
   * Per-hit marker speed increase factor. The effective multiplier each
   * frame is `1 + combo * comboSpeedMultiplier`. Default 0.15. Set to 0
   * to disable combo speedup entirely (tutorial levels).
   */
  comboSpeedMultiplier?: number;
  /** Hit-zone idle color hex. Default COLORS.BIOLUMINESCENT_GREEN (#00ff88). */
  hitZoneColor?: string;
  /** Hit-zone flash color on successful hit. Default COLORS.EMERALD (#00cc88). */
  hitFlashColor?: string;
  /** Hit-zone flash color on miss. Default red (#cc3333). */
  missFlashColor?: string;
}

/**
 * Optional accessor for dynamic per-hit tuning. When supplied via
 * {@link TurnTableTouch.setTuningProvider}, the ability re-reads the
 * tuning on every activation AND while active so Wounds/Medicine
 * updates mid-spin (e.g. a steam-vent damage pulse while the ring is
 * on screen). The provider returns multipliers relative to the config
 * defaults — {@link TurnTableTouch.activate} composes them on top.
 */
export interface TurnTableTouchTuning {
  halfWidthRad: number;
  markerSpeed: number;
  comboMult: number;
}

export class TurnTableTouch implements Ability {
  readonly id = 'turnTableTouch';
  readonly name = 'Turn-Table Touch';
  isActive = false;
  cooldownRemaining = 0;
  readonly cooldownDuration = 0.2; // seconds

  private scene: Scene;

  // Timing ring state
  private ringRadius = 2; // world units
  private readonly visualDepthOffset: number;
  private markerAngle = 0;
  /** Seconds left in the post-miss fumble lockout. */
  private fumbleRemaining = 0;
  /** Lockout after a miss; presses during it count as misses. */
  static readonly FUMBLE_LOCKOUT_SEC = 0.45;
  /** Baseline marker speed (config default). Live speed is `markerSpeed * tuningScale`. */
  private baseMarkerSpeed: number;
  private markerSpeed: number;
  /** Baseline hit-zone half-width in radians (config default). */
  private baseHalfWidth: number;
  private hitZoneStart: number;
  private hitZoneEnd: number;
  /** Baseline combo multiplier. */
  private baseComboMult: number;
  private comboSpeedMultiplier: number;
  private tuningProvider: (() => TurnTableTouchTuning) | null = null;
  private groundedProvider?: () => boolean;

  // Combo tracking
  private combo = 0;
  private maxCombo = 0;
  private lastHitResult: 'none' | 'hit' | 'miss' = 'none';
  private hitFlashTimer = 0;

  // Unified rhythm-ring colors (configurable, defaults from COLORS)
  private hitZoneColor: Color3;
  private hitFlashColor: Color3;
  private missFlashColor: Color3;

  // Callback for successful spin
  private onSpinCallback?: (combo: number) => void;

  // Target position (the dial/gear being interacted with)
  private targetPosition = Vector3.Zero();

  // Visual meshes
  private ringMesh: Mesh | null = null;
  private markerMesh: Mesh | null = null;
  private hitZoneMesh: Mesh | null = null;
  private ringMaterial: StandardMaterial | null = null;
  private markerMaterial: StandardMaterial | null = null;
  private hitZoneMaterial: StandardMaterial | null = null;

  /** Ceiling for the combo-driven marker speed-up (see update()). */
  static readonly MAX_COMBO_SPEED_MULTIPLIER = 1.45;

  constructor(scene: Scene, _basilMesh: AbstractMesh, config?: TurnTableTouchConfig) {
    this.scene = scene;
    this.ringRadius = config?.visualRadius ?? 2;
    this.visualDepthOffset = config?.visualDepthOffset ?? 0;
    this.baseMarkerSpeed = config?.markerSpeedRadPerSec ?? 2.5;
    this.markerSpeed = this.baseMarkerSpeed;
    this.baseHalfWidth = config?.hitZoneHalfWidthRad ?? 0.4;
    this.hitZoneStart = -Math.PI / 2 - this.baseHalfWidth;
    this.hitZoneEnd = -Math.PI / 2 + this.baseHalfWidth;
    this.baseComboMult = config?.comboSpeedMultiplier ?? 0.15;
    this.comboSpeedMultiplier = this.baseComboMult;

    // Unified color scheme — all levels use the same palette unless overridden.
    this.hitZoneColor = Color3.FromHexString(config?.hitZoneColor ?? COLORS.BIOLUMINESCENT_GREEN);
    this.hitFlashColor = Color3.FromHexString(config?.hitFlashColor ?? COLORS.EMERALD);
    this.missFlashColor = Color3.FromHexString(config?.missFlashColor ?? '#cc3333');
  }

  /**
   * Install a dynamic tuning provider (Wounds/Medicine hook). The
   * provider returns the tuning that should override the base config
   * values. Passing `null` restores the configured defaults. Tuning is
   * re-read on each `activate()` so mid-spin state changes don't rewrite
   * the ring geometry under the player's fingers.
   */
  setTuningProvider(provider: (() => TurnTableTouchTuning) | null): void {
    this.tuningProvider = provider;
    // Defer application to the next `activate()` — changing the hit
    // zone / marker speed mid-spin would feel unfair.
  }

  /**
   * Install a grounded provider so Turn-Table Touch cannot activate mid-air.
   * When the provider returns false, both `canActivate()` and `activate()`
   * refuse to start the rhythm minigame (preventing Basil from being frozen
   * suspended in the air). A missing provider defaults to `true` to preserve
   * existing headless/unit behavior.
   */
  setGroundedProvider(fn: () => boolean): void {
    this.groundedProvider = fn;
  }

  private applyTuning(): void {
    const tuning = this.tuningProvider?.();
    // Step 1: resolve Wounds/Medicine tuning (or fall back to config defaults).
    const woundsHalf = tuning?.halfWidthRad ?? this.baseHalfWidth;
    const woundsComboMult = tuning?.comboMult ?? this.baseComboMult;
    this.markerSpeed = tuning?.markerSpeed ?? this.baseMarkerSpeed;
    // Step 2: compose a11y beat-window assist ON TOP of the Wounds value.
    // Re-read per activate() only — no mid-spin listener (no-mid-spin-geometry invariant).
    const beatWindowMultiplier = AccessibilitySystem.getInstance().state.beatWindowMultiplier;
    const resolved = resolveTttWindow(woundsHalf, woundsComboMult, beatWindowMultiplier);
    this.comboSpeedMultiplier = resolved.comboSpeedMultiplier;
    this.hitZoneStart = -Math.PI / 2 - resolved.halfWidthRad;
    this.hitZoneEnd = -Math.PI / 2 + resolved.halfWidthRad;
  }

  canActivate(): boolean {
    return !this.isActive && this.cooldownRemaining <= 0 && (this.groundedProvider?.() ?? true);
  }

  /**
   * Stage the target position + per-spin callback for the next activation.
   * Call this, then activate via the owning AbilityHolder
   * (`basilAbilities.activateAbility("turnTableTouch")`) so
   * ABILITY_ACTIVATED telemetry/HUD/banter fire.
   */
  setTarget(position: Vector3, onSpin?: (combo: number) => void): void {
    this.targetPosition = position.clone();
    this.onSpinCallback = onSpin;
  }

  /**
   * Begin the rhythm interaction at a target position.
   *
   * @deprecated Use {@link setTarget} + `AbilityHolder.activateAbility` —
   * direct activation bypasses ABILITY_ACTIVATED (HUD slot pulse, activate
   * sfx, banter, and the playthrough-recorder ability timeline all key off
   * that event). Kept as a thin wrapper for direct unit tests.
   */
  activateAt(position: Vector3, onSpin?: (combo: number) => void): boolean {
    this.setTarget(position, onSpin);
    return this.activate();
  }

  activate(): boolean {
    if (!this.canActivate()) return false;
    if (!(this.groundedProvider?.() ?? true)) return false;

    // Re-apply tuning before creating visuals so a Wounded Basil (for
    // example) sees a wider hit window + slower marker on the very
    // first frame of the ring.
    this.applyTuning();

    this.isActive = true;
    this.markerAngle = 0;
    this.fumbleRemaining = 0;
    this.combo = 0;
    this.lastHitResult = 'none';

    this.createVisuals();

    return true;
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    // Advance the marker around the ring (speed increases with combo).
    // The combo speed-up is capped: with the default 2.5 rad/s marker and
    // ±0.4 rad hit zone, an uncapped combo run (e.g. GearB's 6 required
    // spins) shrank the hit window to ~183 ms by combo 5 — and dials that
    // only advance on real hits let a low-rhythm player stall there
    // indefinitely. The cap keeps the fastest window at ~220 ms, still an
    // escalation but reachable without frame-perfect timing.
    const speedMultiplier = Math.min(
      TurnTableTouch.MAX_COMBO_SPEED_MULTIPLIER,
      1 + this.combo * this.comboSpeedMultiplier,
    );
    this.markerAngle += this.markerSpeed * speedMultiplier * deltaTime;
    if (this.markerAngle > Math.PI * 2) {
      this.markerAngle -= Math.PI * 2;
    }

    // Decay hit flash and fumble lockout
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= deltaTime;
    }
    if (this.fumbleRemaining > 0) {
      this.fumbleRemaining = Math.max(0, this.fumbleRemaining - deltaTime);
    }

    this.updateVisuals();
  }

  deactivate(): void {
    this.isActive = false;
    this.cooldownRemaining = this.cooldownDuration;
    this.maxCombo = Math.max(this.maxCombo, this.combo);
    this.combo = 0;
    this.onSpinCallback = undefined;

    this.disposeVisuals();
  }

  /**
   * Call this when the player presses the interact key during the rhythm game.
   * Returns true if the timing was good.
   */
  attemptHit(): boolean {
    if (!this.isActive) return false;

    // Normalize marker angle to [-PI, PI] range for comparison
    let normalizedAngle = this.markerAngle % (Math.PI * 2);
    if (normalizedAngle > Math.PI) normalizedAngle -= Math.PI * 2;

    // Check if marker is in the hit zone. A press during the fumble
    // lockout after a miss is itself a miss (and restarts the lockout), so
    // mashing never lands a hit — playtest found mashing locked the L0 dial
    // twice as fast as reading the marker. One press per pass is unaffected.
    const fumbling = this.fumbleRemaining > 0;
    const inHitZone = !fumbling && normalizedAngle >= this.hitZoneStart && normalizedAngle <= this.hitZoneEnd;

    if (inHitZone) {
      this.combo++;
      this.lastHitResult = 'hit';
      this.hitFlashTimer = 0.3;

      // Trigger the spin callback
      if (this.onSpinCallback) {
        this.onSpinCallback(this.combo);
      }

      this.emitSuccessParticles();

      gameEvents.emit(GameEvents.TURN_TABLE_TOUCH_HIT, {
        combo: this.combo,
        targetPosition: this.targetPosition,
      });

      return true;
    } else {
      this.combo = 0;
      this.lastHitResult = 'miss';
      this.hitFlashTimer = 0.3;
      // QA rules (?qaRules=1) test puzzle logic, not rhythm skill: their
      // recipes press on a fixed cadence, so no lockout there.
      if (!qaRulesActive()) this.fumbleRemaining = TurnTableTouch.FUMBLE_LOCKOUT_SEC;

      gameEvents.emit(GameEvents.TURN_TABLE_TOUCH_MISS, {
        targetPosition: this.targetPosition,
      });

      return false;
    }
  }

  get currentCombo(): number {
    return this.combo;
  }

  private createVisuals(): void {
    // Anchor the ring directly on the target instead of floating it
    // 2.5u overhead. At that elevation the ring reads as visually
    // disconnected from the dial/gear, especially from the shared-
    // camera framing that includes both cats.
    const displayPos = this.targetPosition.clone();
    displayPos.z += this.visualDepthOffset;

    // Main timing ring (torus)
    this.ringMesh = CreateTorus('turnTableRing', {
      diameter: this.ringRadius * 2,
      thickness: 0.05,
      tessellation: 64,
    }, this.scene);
    this.ringMesh.position = displayPos;
    this.ringMesh.rotation.x = Math.PI / 2; // Face the camera (XY plane)
    this.ringMesh.isPickable = false;

    this.ringMaterial = new StandardMaterial('turnTableRingMat', this.scene);
    this.ringMaterial.emissiveColor = Color3.FromHexString(COLORS.BRASS);
    this.ringMaterial.disableLighting = true;
    this.ringMesh.material = this.ringMaterial;

    // Orbiting marker (small sphere)
    this.markerMesh = CreateSphere('turnTableMarker', {
      diameter: 0.3,
      segments: 16,
    }, this.scene);
    this.markerMesh.isPickable = false;

    this.markerMaterial = new StandardMaterial('turnTableMarkerMat', this.scene);
    this.markerMaterial.emissiveColor = Color3.FromHexString(COLORS.COPPER);
    this.markerMaterial.disableLighting = true;
    this.markerMesh.material = this.markerMaterial;

    // The visible target is exactly the resolved timing window, not a
    // full green circle. applyTuning() resolves assistance before activation.
    const hitPath = Array.from({ length: 33 }, (_, i) => {
      const angle = this.hitZoneStart + (this.hitZoneEnd - this.hitZoneStart) * i / 32;
      return new Vector3(Math.cos(angle) * this.ringRadius, Math.sin(angle) * this.ringRadius, 0);
    });
    this.hitZoneMesh = CreateTube('turnTableHitZone', {
      path: hitPath,
      radius: 0.06,
      tessellation: 8,
      cap: Mesh.CAP_ALL,
    }, this.scene);
    this.hitZoneMesh.position = displayPos;
    this.hitZoneMesh.isPickable = false;

    this.hitZoneMaterial = new StandardMaterial('turnTableHitZoneMat', this.scene);
    this.hitZoneMaterial.emissiveColor = this.hitZoneColor;
    this.hitZoneMaterial.alpha = 0.4;
    this.hitZoneMaterial.disableLighting = true;
    this.hitZoneMesh.material = this.hitZoneMaterial;
  }

  private updateVisuals(): void {
    if (!this.ringMesh || !this.markerMesh || !this.hitZoneMesh) return;

    // Anchor the ring directly on the target instead of floating it
    // 2.5u overhead. At that elevation the ring reads as visually
    // disconnected from the dial/gear, especially from the shared-
    // camera framing that includes both cats.
    const displayPos = this.targetPosition.clone();
    displayPos.z += this.visualDepthOffset;

    // Update ring position (in case target moves)
    this.ringMesh.position.copyFrom(displayPos);
    this.hitZoneMesh.position.copyFrom(displayPos);

    // Update marker position (orbiting the ring in XY plane)
    const mx = displayPos.x + Math.cos(this.markerAngle) * this.ringRadius;
    const my = displayPos.y + Math.sin(this.markerAngle) * this.ringRadius;
    this.markerMesh.position.set(mx, my, displayPos.z);

    // Flash hit zone on hit/miss — uses unified color fields from config.
    if (this.hitFlashTimer > 0 && this.hitZoneMaterial) {
      if (this.lastHitResult === 'hit') {
        this.hitZoneMaterial.emissiveColor = this.hitFlashColor;
        this.hitZoneMaterial.alpha = 0.8;
      } else if (this.lastHitResult === 'miss') {
        this.hitZoneMaterial.emissiveColor = this.missFlashColor;
        this.hitZoneMaterial.alpha = 0.8;
      }
    } else if (this.hitZoneMaterial) {
      this.hitZoneMaterial.emissiveColor = this.hitZoneColor;
      this.hitZoneMaterial.alpha = 0.4;
    }
  }

  private emitSuccessParticles(): void {
    const particles = new ParticleSystem('turnTableHitParticles', 20, this.scene);
    particles.emitter = this.markerMesh ?? this.targetPosition;
    particles.minSize = 0.05;
    particles.maxSize = 0.15;
    particles.minLifeTime = 0.2;
    particles.maxLifeTime = 0.5;
    particles.emitRate = 50;
    particles.minEmitPower = 1;
    particles.maxEmitPower = 3;
    particles.direction1 = new Vector3(-1, -1, -1);
    particles.direction2 = new Vector3(1, 1, 1);
    particles.color1 = new Color4(0.77, 0.61, 0.24, 1); // BRASS
    particles.color2 = new Color4(0, 0.8, 0.53, 1); // EMERALD
    particles.colorDead = new Color4(1, 1, 1, 0);
    particles.blendMode = ParticleSystem.BLENDMODE_ADD;
    particles.createPointEmitter(new Vector3(-0.5, -0.5, -0.5), new Vector3(0.5, 0.5, 0.5));

    particles.start();

    // Auto-cleanup after a short burst
    setTimeout(() => {
      particles.stop();
      particles.dispose();
    }, 500);
  }

  private disposeVisuals(): void {
    this.ringMesh?.dispose();
    this.ringMesh = null;

    this.markerMesh?.dispose();
    this.markerMesh = null;

    this.hitZoneMesh?.dispose();
    this.hitZoneMesh = null;

    this.ringMaterial?.dispose();
    this.ringMaterial = null;

    this.markerMaterial?.dispose();
    this.markerMaterial = null;

    this.hitZoneMaterial?.dispose();
    this.hitZoneMaterial = null;
  }
}
